import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, ChildProfile, AppMode, PracticeSession } from '../types';

interface AppState {
  user: User | null;
  appMode: AppMode;
  activeChildId: string | null;
  practiceSession: PracticeSession | null;
  isLoading: boolean;

  setUser: (user: User | null) => void;
  logout: () => void;
  setTier: (tier: 'guest' | 'free' | 'premium') => void;
  setAppMode: (mode: 'parent' | 'kid') => void;
  setActiveChild: (childId: string | null) => void;
  getActiveChild: () => ChildProfile | null;
  setPracticeSession: (session: PracticeSession | null) => void;
  markScenarioPracticed: (scenarioId: string) => void;
  toggleSavedScenario: (scenarioId: string) => void;
  toggleSavedAffirmation: (affirmationId: string) => void;
  addPoints: (points: number) => void;
  awardBadge: (badgeId: string) => void;
  updateStreak: () => void;
  setLoading: (loading: boolean) => void;
}

const createDefaultUser = (): User => ({
  id: 'user-' + Date.now(),
  email: '',
  name: 'Parent',
  role: 'parent',
  tier: 'free',
  points: 0,
  earnedBadgeIds: [],
  childProfiles: [],
  savedScenarioIds: [],
  createdBundleIds: [],
  contributionCount: 0,
  createdAt: new Date().toISOString(),
});

export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      user: null,
      appMode: { mode: 'parent' },
      activeChildId: null,
      practiceSession: null,
      isLoading: false,

      setUser: (user) => set({ user }),

      logout: () => set({ user: null, activeChildId: null, appMode: { mode: 'parent' } }),

      setTier: (tier) => {
        const { user } = get();
        if (!user) return;
        set({ user: { ...user, tier } });
      },

      setAppMode: (mode) =>
        set((state) => ({ appMode: { ...state.appMode, mode } })),

      setActiveChild: (childId) =>
        set({ activeChildId: childId }),

      getActiveChild: () => {
        const { user, activeChildId } = get();
        if (!user || !activeChildId) return null;
        return user.childProfiles.find((c) => c.id === activeChildId) ?? null;
      },

      setPracticeSession: (session) => set({ practiceSession: session }),

      markScenarioPracticed: (scenarioId) => {
        const { user, activeChildId } = get();
        if (!user) return;

        if (activeChildId) {
          const updatedProfiles = user.childProfiles.map((child) =>
            child.id === activeChildId
              ? {
                  ...child,
                  practicedScenarioIds: Array.from(
                    new Set([...child.practicedScenarioIds, scenarioId])
                  ),
                  points: child.points + 10,
                  lastPracticeDate: new Date().toISOString(),
                }
              : child
          );
          set({ user: { ...user, childProfiles: updatedProfiles } });
        }
      },

      toggleSavedScenario: (scenarioId) => {
        const { user } = get();
        if (!user) return;
        const saved = user.savedScenarioIds.includes(scenarioId)
          ? user.savedScenarioIds.filter((id) => id !== scenarioId)
          : [...user.savedScenarioIds, scenarioId];
        set({ user: { ...user, savedScenarioIds: saved } });
      },

      toggleSavedAffirmation: (affirmationId) => {
        const { user } = get();
        if (!user) return;
        const current = user.savedAffirmationIds ?? [];
        const saved = current.includes(affirmationId)
          ? current.filter((id) => id !== affirmationId)
          : [...current, affirmationId];
        set({ user: { ...user, savedAffirmationIds: saved } });
      },

      addPoints: (points) => {
        const { user } = get();
        if (!user) return;
        set({ user: { ...user, points: user.points + points } });
      },

      awardBadge: (badgeId) => {
        const { user, activeChildId } = get();
        if (!user) return;

        if (activeChildId) {
          const updatedProfiles = user.childProfiles.map((child) =>
            child.id === activeChildId && !child.earnedBadgeIds.includes(badgeId)
              ? { ...child, earnedBadgeIds: [...child.earnedBadgeIds, badgeId] }
              : child
          );
          set({ user: { ...user, childProfiles: updatedProfiles } });
        } else if (!user.earnedBadgeIds.includes(badgeId)) {
          set({ user: { ...user, earnedBadgeIds: [...user.earnedBadgeIds, badgeId] } });
        }
      },

      updateStreak: () => {
        const { user, activeChildId } = get();
        if (!user || !activeChildId) return;

        const updatedProfiles = user.childProfiles.map((child) => {
          if (child.id !== activeChildId) return child;
          const lastDate = child.lastPracticeDate
            ? new Date(child.lastPracticeDate).toDateString()
            : null;
          const isConsecutive =
            lastDate === new Date(Date.now() - 86400000).toDateString();
          return {
            ...child,
            streak: isConsecutive ? child.streak + 1 : 1,
            lastPracticeDate: new Date().toISOString(),
          };
        });
        set({ user: { ...user, childProfiles: updatedProfiles } });
      },

      setLoading: (loading) => set({ isLoading: loading }),
    }),
    {
      name: 'lifeskills-app-store',
      partialize: (state) => ({
        user: state.user,
        appMode: state.appMode,
        activeChildId: state.activeChildId,
      }),
    }
  )
);

export const initGuestUser = () => {
  const { user, setUser } = useAppStore.getState();
  if (!user) setUser(createDefaultUser());
};
